import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable} from 'rxjs';
import {environment} from '../../env';
import { AppLogger, AppLoggerService } from '../../core/logging';
import { RecipeFacets } from './recipe-facets.models';

/**
 * Filters for the recipe list endpoint. All are optional and combined with AND
 * server-side. Values are facet KEYS (not labels). `slot` expands to eligible
 * courses server-side (e.g. slot=dinner → recipes whose course fits dinner).
 */
export interface RecipeListFilters {
  course?: string;
  season?: string;
  dishType?: string;
  slot?: string;
}

export interface RecipePreviewRequest {
  title: string;
  servings: number;
  prep_time_minutes: number;
  steps: {
    order: number;
    type: string;
    [key: string]: any; // Allow any additional fields based on step type
  }[];
}

/**
 * Aggregate rating for a recipe (read-only).
 * `average` is 0 when unrated and rounded to 1 decimal server-side.
 */
export interface RatingSummary {
  average: number;
  count: number;
}

/** The current user's own rating on a recipe. */
export interface MyRating {
  score: number;     // integer 1..5
  comment?: string;  // omitted/empty when none
}

/** A single row in the comment view (GET /recipes/:id/ratings). */
export interface RatingListItem {
  user_id: number;
  user_name: string;
  user_email: string;
  picture_url?: string;
  score: number;
  comment?: string;
  created_at: string; // RFC3339
  updated_at: string;
}

/**
 * How a recipe came to exist — set once at creation, never changes on edit.
 * "import" always carries `url` (the page it was extracted from); "user" and
 * "assistant" never do.
 */
export interface RecipeSourceDto {
  type: 'user' | 'import' | 'assistant' | string;
  url?: string;
}

export interface RecipeListItem {
  id: number;
  title: string;
  servings?: number;
  prep_time_minutes?: number;
  /** Read-only "on the table in" total (prep + all cook/wait step times),
   *  derived and stored server-side. */
  total_time_minutes?: number;
  /** The owning household. Every member can view and edit. */
  household_id?: number;
  /** Publicly readable (read-only discovery) when true. */
  is_public?: boolean;
  source?: RecipeSourceDto;
  updated_at?: string;
  /** The creator, or null when that account has been deleted
   *  (render "Deleted user"). */
  created_by?: {
    user_id: number;
    user_email?: string | null;
    user_name?: string | null;
  } | null;
  rating?: RatingSummary;
  facets?: RecipeFacets;
  /** Short-lived signed URL of the recipe's primary thumbnail (400px), or
   *  absent when it has no image. */
  thumbnail_url?: string;
}

export type RecipesListResponse = RecipeListItem[];

export type RecipeStepDto = {
  order: number;
  type: 'instruction' | 'cook' | 'wait' | 'serve' | string;
  text?: string;
  text_html?: string;

  // cook
  device?: 'oven' | 'pan' | 'pot' | 'grill' | 'airfryer' | string;
  mode?: 'top_bottom_heat' | 'convection' | 'grill' | string;
  temp_c?: number;
  heat_level?: number;
  time_min?: number;
  preheat?: boolean;
  rack_position?: number;
  grill_mode?: 'direct' | 'indirect' | string;

  // wait
  location?: 'room' | 'fridge' | string;
  covered?: boolean;

  // allow backend to evolve
  [key: string]: any;
};

export type IngredientQuantityKind = 'exact' | 'qual' | 'range' | string;

export interface IngredientQuantityDto {
  kind: IngredientQuantityKind;
  readable: string;
  unit?: string;
  amount?: number;

  // allow backend to evolve
  [key: string]: any;
}

export interface RecipeIngredient {
  name: string;
  quantity?: IngredientQuantityDto;
}

export interface RecipeIngredientMentionDto {
  id?: number;
  step_id?: number;
  span_start?: number;
  span_end?: number;
  token_start_index?: number;
  token_end_index?: number;
  ingredient_raw?: string;
  quantity?: IngredientQuantityDto;
  is_active?: boolean;

  // allow backend to evolve
  [key: string]: any;
}

export interface RecipeIngredientDto {
  id?: number;
  key?: string;
  display_name?: string;
  canonical?: string;
  is_active?: boolean;
  mentions?: RecipeIngredientMentionDto[];

  /**
   * New backend aggregate info per ingredient.
   * Can be null if the backend couldn't aggregate quantities.
   */
  aggregate?: {
    quantity?: IngredientQuantityDto | null;
    step_ids?: number[] | null;

    // allow backend to evolve
    [key: string]: any;
  } | null;

  product_candidates?: any[];

  // allow backend to evolve
  [key: string]: any;
}

export interface RecipeDetailDto {
  id: number;
  title: string;
  /** Portion count this response is scaled to (equals base_servings unless a
   *  `servings` query requested a different size). */
  servings?: number;
  /** Portion count the recipe was authored for. Drives the portion selector. */
  base_servings?: number;
  /** True when quantities have been rescaled for `servings` (≠ base_servings). */
  scaled?: boolean;
  prep_time_minutes?: number;
  /** Read-only "on the table in" total (prep + all cook/wait step times),
   *  derived and stored server-side. */
  total_time_minutes?: number;
  /** The owning household. */
  household_id?: number;
  /** Publicly readable (read-only) when true. */
  is_public?: boolean;
  /** The creator, or 0 when that account was deleted (render "Deleted user"). */
  created_by_user_id?: number;
  /** The creator's identity, when the backend enriches it (as on the list).
   *  null when the account has been deleted. */
  created_by?: {
    user_id: number;
    user_email?: string | null;
    user_name?: string | null;
  } | null;
  /** Whether the current user may edit this recipe (household member). */
  can_edit?: boolean;
  source?: RecipeSourceDto;
  steps: RecipeStepDto[];
  aggregated_ingredients?: RecipeIngredient[];
  ingredients?: RecipeIngredientDto[];
  created_at?: string;
  updated_at?: string;
  rating?: RatingSummary;
  my_rating?: MyRating;
  facets?: RecipeFacets;
  /**
   * ID of the meal product created from this recipe's leftovers, or null/absent
   * when none exists. Drives the "Save as meal" vs "View meal" action.
   */
  meal_product_id?: number | null;
  /** Signed URL of the recipe's title image at full size (1024px) for the
   *  detail hero; thumbnail_url is the 400px variant. Absent when no image. */
  image_url?: string;
  thumbnail_url?: string;
}

/** Response of POST /recipes/:id/meal — the meal product to navigate to. */
export interface RecipeMealResponse {
  product_id: number;
}

export interface RecipeCreateRequest {
  title: string;
  servings: number;
  prep_time_minutes: number;
  /** Owning household for a new recipe. Optional — the backend uses the
   *  actor's default household when omitted. Ignored on update. */
  household_id?: number;
  /** Mark the recipe publicly readable. */
  is_public?: boolean;
  steps: {
    order: number;
    type: string;
    [key: string]: any;
  }[];
}

// Backend response is not specified yet; keep it flexible but useful.
export interface RecipeCreateResponse {
  id?: number;

  [key: string]: any;
}

export interface RecipePreviewResponse {
  by_step: {
    [stepNumber: string]: RecipeIngredient[];
  };
  aggregated: RecipeIngredient[];
}

@Injectable({
  providedIn: 'root'
})
export class RecipeService {
  private readonly tag = '[RecipeService]';

  private readonly log: AppLogger;

  constructor(
    private http: HttpClient,
    private appLogger: AppLoggerService
  ) {
    this.log = this.appLogger.createForClass(RecipeService);
  }

  /**
   * Preview ingredients from recipe steps
   * POST /api/recipes/ingredients-preview
   */
  previewIngredients(request: RecipePreviewRequest): Observable<RecipePreviewResponse> {
    this.log.debug('Preview recipe ingredients', { request });

    return this.http.post<RecipePreviewResponse>(
      `${environment.apiUrl}/recipes/ingredients-preview`,
      request
    );
  }

  /**
   * Create a new recipe
   * POST /api/recipes
   */
  createRecipe(request: RecipeCreateRequest): Observable<RecipeCreateResponse> {
    this.log.debug('Create recipe', { request });

    return this.http.post<RecipeCreateResponse>(
      `${environment.apiUrl}/recipes`,
      request
    );
  }

  /**
   * Update an existing recipe
   * PUT /api/recipes/:id
   */
  updateRecipe(id: number, request: RecipeCreateRequest): Observable<RecipeCreateResponse> {
    this.log.debug('Update recipe', { id, request });

    return this.http.put<RecipeCreateResponse>(
      `${environment.apiUrl}/recipes/${id}`,
      request
    );
  }

  /**
   * Fetch recipes
   * GET /api/recipes
   * Optional query: view=mine, q=<search> (server-side title/owner filter)
   */
  getRecipes(view?: 'mine', q?: string): Observable<RecipesListResponse> {
    const params: Record<string, string> = {};
    if (view) {
      params['view'] = view;
    }
    const query = q?.trim();
    if (query) {
      params['q'] = query;
    }

    this.log.debug('Get recipes', { view, q: query });

    return this.http.get<RecipesListResponse>(
      `${environment.apiUrl}/recipes`,
      {params}
    );
  }

  /**
   * Fetch a single recipe
   * GET /api/recipes/:id
   *
   * When `servings` is a positive number different from the recipe's own
   * servings, the backend returns the recipe with every ingredient quantity
   * rescaled for that portion count (view-only; nothing is persisted).
   */
  getRecipeById(id: number, servings?: number): Observable<RecipeDetailDto> {
    this.log.debug('Get recipe by id', { id, servings });

    const params: Record<string, string> = {};
    if (typeof servings === 'number' && Number.isFinite(servings) && servings > 0) {
      params['servings'] = String(Math.round(servings));
    }

    return this.http.get<RecipeDetailDto>(
      `${environment.apiUrl}/recipes/${id}`,
      { params }
    );
  }

  /**
   * Fetch recipes for a household
   * GET /api/households/:id/recipes
   * Optional query: view=mine, q=<search> (server-side title/owner filter),
   * plus facet filters (course/season/dish_type/slot — keys, AND-combined).
   */
  getHouseholdRecipes(
    householdId: number,
    view?: 'mine',
    q?: string,
    filters?: RecipeListFilters,
  ): Observable<RecipesListResponse> {
    const params: Record<string, string> = {};
    if (view) {
      params['view'] = view;
    }
    const query = q?.trim();
    if (query) {
      params['q'] = query;
    }
    if (filters?.course) {
      params['course'] = filters.course;
    }
    if (filters?.season) {
      params['season'] = filters.season;
    }
    if (filters?.dishType) {
      params['dish_type'] = filters.dishType;
    }
    if (filters?.slot) {
      params['slot'] = filters.slot;
    }

    this.log.debug('Get household recipes', { householdId, view, q: query, filters });

    return this.http.get<RecipesListResponse>(
      `${environment.apiUrl}/households/${householdId}/recipes`,
      { params }
    );
  }

  /**
   * Fetch "cook now" recipe suggestions for a household — up to `limit` random
   * main-course recipes for the current season (or the `season` override),
   * topped up with other mains when the season is sparse.
   * GET /api/households/:id/recipes/suggestions?season=&limit=
   * Each call returns a fresh random set (used to power the shuffle action).
   */
  getRecipeSuggestions(
    householdId: number,
    season?: string,
    limit?: number,
  ): Observable<RecipesListResponse> {
    const params: Record<string, string> = {};
    const s = season?.trim();
    if (s) {
      params['season'] = s;
    }
    if (limit && limit > 0) {
      params['limit'] = String(limit);
    }

    this.log.debug('Get recipe suggestions', { householdId, season: s, limit });

    return this.http.get<RecipesListResponse>(
      `${environment.apiUrl}/households/${householdId}/recipes/suggestions`,
      { params }
    );
  }

  /**
   * Delete a recipe
   * DELETE /api/recipes/:id
   */
  deleteRecipe(id: number): Observable<void> {
    this.log.debug('Delete recipe', { id });

    return this.http.delete<void>(
      `${environment.apiUrl}/recipes/${id}`
    );
  }

  /**
   * Create (or fetch the existing) meal product from a recipe's leftovers.
   * POST /api/recipes/:id/meal — idempotent: returns the existing meal if one
   * already exists, otherwise creates it. Returns the meal product's id.
   */
  createMeal(recipeId: number): Observable<RecipeMealResponse> {
    this.log.debug('Create meal from recipe', { recipeId });

    return this.http.post<RecipeMealResponse>(
      `${environment.apiUrl}/recipes/${recipeId}/meal`,
      {}
    );
  }
}
